/**
 * Pure polyline geometry.
 *
 * Nothing here knows about graphs, nodes or ports — these are the primitives the
 * router builds on and the renderer consumes. Keeping them free of graph
 * knowledge is what makes the orthogonality invariant cheap to state and cheap
 * to test.
 *
 * The single rule the whole diagram depends on: a connector is a sequence of
 * horizontal and vertical segments. Never a diagonal.
 */
import type { Point, Rect } from '../model/types';

/**
 * Tolerance for "these two coordinates are the same line". Float arithmetic in
 * the router (midpoints, halved sizes) produces noise several orders of
 * magnitude below any visible distance.
 */
export const EPSILON = 1e-6;

/** Coordinates are snapped to this many decimals, well below one device pixel. */
const COORDINATE_DECIMALS = 3;
const COORDINATE_SCALE = 10 ** COORDINATE_DECIMALS;

export interface PolylineSegment {
  readonly a: Point;
  readonly b: Point;
  readonly axis: 'h' | 'v';
}

/**
 * Snaps a coordinate onto the shared grid. Two values that differ only by float
 * noise land on the same number, which is what lets `===` be the orthogonality
 * test everywhere else. Non-finite input collapses to 0 so a single bad number
 * upstream can never leak `NaN` into an SVG path.
 */
export function quantize(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const snapped = Math.round(value * COORDINATE_SCALE) / COORDINATE_SCALE;
  // Collapse -0, which compares equal to 0 but is not structurally equal to it.
  return snapped === 0 ? 0 : snapped;
}

/** True when every consecutive pair of points shares an x or a y. */
export function isOrthogonal(points: readonly Point[]): boolean {
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    if (a.x !== b.x && a.y !== b.y) return false;
  }
  return true;
}

/**
 * Snaps coordinates, drops zero-length segments and drops points that merely
 * sit in the middle of a longer straight run. Idempotent: normalizing an
 * already-normalized polyline returns a structurally equal result.
 */
export function normalizePolyline(points: readonly Point[]): Point[] {
  const out: Point[] = [];
  for (const point of points) {
    appendNormalized(out, { x: quantize(point.x), y: quantize(point.y) });
  }
  return out;
}

/**
 * Adds `next` to `out`, collapsing it against the tail. The loop repeats
 * because removing one redundant point can expose another — a run that doubles
 * back on itself unwinds one point at a time.
 */
function appendNormalized(out: Point[], next: Point): void {
  for (;;) {
    const count = out.length;
    if (count === 0) break;
    const last = out[count - 1];
    if (last.x === next.x && last.y === next.y) return;
    if (count >= 2) {
      const previous = out[count - 2];
      const collinear =
        (previous.x === last.x && last.x === next.x) ||
        (previous.y === last.y && last.y === next.y);
      if (collinear) {
        out.pop();
        continue;
      }
    }
    break;
  }
  out.push(next);
}

/**
 * Repairs an arbitrary polyline into an orthogonal one by inserting an elbow at
 * every diagonal segment, travelling horizontally first. This is the safety
 * net: whatever happens upstream, the renderer receives orthogonal points.
 */
export function orthogonalize(points: readonly Point[]): Point[] {
  if (points.length === 0) return [];
  const first = points[0];
  const out: Point[] = [{ x: first.x, y: first.y }];
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    if (a.x !== b.x && a.y !== b.y) out.push({ x: b.x, y: a.y });
    out.push({ x: b.x, y: b.y });
  }
  return out;
}

export function polylineLength(points: readonly Point[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    total += distance(points[i - 1], points[i]);
  }
  return total;
}

/** Number of interior points where the polyline changes direction. */
export function countBends(points: readonly Point[]): number {
  let bends = 0;
  for (let i = 1; i + 1 < points.length; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    const c = points[i + 1];
    const cross = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
    if (Math.abs(cross) > EPSILON) bends += 1;
  }
  return bends;
}

/**
 * SVG path data. With `cornerRadius > 0` each 90-degree corner becomes a
 * quadratic arc whose radius is clamped to half of the shorter adjacent
 * segment, so short segments never overshoot into their neighbour. The geometry
 * between corners stays orthogonal — the arc is a rendering nicety only.
 */
export function polylineToPath(points: readonly Point[], cornerRadius = 0): string {
  if (points.length === 0) return '';
  const first = points[0];
  const start = `M ${coord(first.x)} ${coord(first.y)}`;
  if (points.length === 1) return start;

  const radius = Number.isFinite(cornerRadius) ? Math.max(0, cornerRadius) : 0;
  if (radius <= 0) {
    let straight = start;
    for (let i = 1; i < points.length; i += 1) {
      straight += ` L ${coord(points[i].x)} ${coord(points[i].y)}`;
    }
    return straight;
  }

  let d = start;
  for (let i = 1; i + 1 < points.length; i += 1) {
    const a = points[i - 1];
    const corner = points[i];
    const c = points[i + 1];
    const inLength = distance(a, corner);
    const outLength = distance(corner, c);
    if (inLength <= EPSILON || outLength <= EPSILON) continue;

    const inX = (corner.x - a.x) / inLength;
    const inY = (corner.y - a.y) / inLength;
    const outX = (c.x - corner.x) / outLength;
    const outY = (c.y - corner.y) / outLength;
    if (Math.abs(inX - outX) <= EPSILON && Math.abs(inY - outY) <= EPSILON) {
      d += ` L ${coord(corner.x)} ${coord(corner.y)}`;
      continue;
    }

    const r = Math.min(radius, inLength / 2, outLength / 2);
    d += ` L ${coord(corner.x - inX * r)} ${coord(corner.y - inY * r)}`;
    d += ` Q ${coord(corner.x)} ${coord(corner.y)} ${coord(corner.x + outX * r)} ${coord(corner.y + outY * r)}`;
  }

  const last = points[points.length - 1];
  return `${d} L ${coord(last.x)} ${coord(last.y)}`;
}

/** Point at fraction `t` of the total length, clamped to the polyline's ends. */
export function pointOnPolyline(points: readonly Point[], t: number): Point {
  if (points.length === 0) return { x: 0, y: 0 };
  const first = points[0];
  if (points.length === 1) return { x: first.x, y: first.y };

  const total = polylineLength(points);
  if (total <= EPSILON) return { x: first.x, y: first.y };

  const fraction = Number.isFinite(t) ? Math.min(1, Math.max(0, t)) : 0;
  let remaining = total * fraction;
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    const length = distance(a, b);
    if (length <= EPSILON) continue;
    if (remaining <= length) {
      const f = remaining / length;
      return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f };
    }
    remaining -= length;
  }
  const last = points[points.length - 1];
  return { x: last.x, y: last.y };
}

/**
 * Segments with their axis. A zero-length segment is reported as horizontal;
 * a segment that is not exactly axis-aligned is classified by its dominant
 * delta, which keeps callers safe on unrepaired input.
 */
export function segmentsOf(points: readonly Point[]): PolylineSegment[] {
  const out: PolylineSegment[] = [];
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    const axis: 'h' | 'v' = Math.abs(b.y - a.y) <= Math.abs(b.x - a.x) ? 'h' : 'v';
    out.push({ a, b, axis });
  }
  return out;
}

/**
 * True when the segment passes through the interior of `rect` grown by
 * `padding`. Running exactly along the padded boundary does not count — that is
 * precisely the lane the router wants to use.
 */
export function segmentIntersectsRect(a: Point, b: Point, rect: Rect, padding = 0): boolean {
  const minX = rect.x - padding;
  const minY = rect.y - padding;
  const maxX = rect.x + rect.width + padding;
  const maxY = rect.y + rect.height + padding;
  if (maxX - minX <= EPSILON * 2 || maxY - minY <= EPSILON * 2) return false;

  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const p = [-dx, dx, -dy, dy];
  const q = [a.x - minX, maxX - a.x, a.y - minY, maxY - a.y];

  // Liang-Barsky clipping: find the parameter window the segment spends inside.
  let t0 = 0;
  let t1 = 1;
  for (let i = 0; i < 4; i += 1) {
    if (p[i] === 0) {
      if (q[i] < 0) return false;
      continue;
    }
    const r = q[i] / p[i];
    if (p[i] < 0) {
      if (r > t1) return false;
      if (r > t0) t0 = r;
    } else {
      if (r < t0) return false;
      if (r < t1) t1 = r;
    }
  }

  // The window can be a boundary graze; only an interior midpoint is a real hit.
  const mid = (t0 + t1) / 2;
  const mx = a.x + dx * mid;
  const my = a.y + dy * mid;
  return mx > minX + EPSILON && mx < maxX - EPSILON && my > minY + EPSILON && my < maxY - EPSILON;
}

/** True when any segment of the polyline enters the padded rectangle. */
export function polylineIntersectsRect(points: readonly Point[], rect: Rect, padding = 0): boolean {
  for (let i = 1; i < points.length; i += 1) {
    if (segmentIntersectsRect(points[i - 1], points[i], rect, padding)) return true;
  }
  return false;
}

/**
 * Number of places where two orthogonal polylines cross. Only perpendicular
 * segment pairs can cross; touching at an endpoint (a T-junction) is not a
 * crossing.
 *
 * Passing the same polyline twice counts every self-crossing exactly twice,
 * once per ordering of the pair.
 */
export function countCrossings(a: readonly Point[], b: readonly Point[]): number {
  const first = segmentsOf(a);
  const second = segmentsOf(b);
  let count = 0;
  for (const s of first) {
    for (const t of second) {
      if (s.axis === t.axis) continue;
      const horizontal = s.axis === 'h' ? s : t;
      const vertical = s.axis === 'v' ? s : t;
      if (crossesPerpendicular(horizontal, vertical)) count += 1;
    }
  }
  return count;
}

function crossesPerpendicular(horizontal: PolylineSegment, vertical: PolylineSegment): boolean {
  const y = horizontal.a.y;
  const x = vertical.a.x;
  const left = Math.min(horizontal.a.x, horizontal.b.x);
  const right = Math.max(horizontal.a.x, horizontal.b.x);
  const top = Math.min(vertical.a.y, vertical.b.y);
  const bottom = Math.max(vertical.a.y, vertical.b.y);
  return x > left + EPSILON && x < right - EPSILON && y > top + EPSILON && y < bottom - EPSILON;
}

function distance(a: Point, b: Point): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

function coord(value: number): string {
  return String(quantize(value));
}
